import { t } from 'i18next';

import { toastActions } from '@/entities/toast';
import { boatServiceApi } from '@/shared/api/boat-service.api';
import { userServiceApi } from '@/shared/api/user-service.api';
import { AppStoreTag } from '@/shared/constants/app-store-tag.constant';
import { convertTimezone } from '@/shared/lib/helpers/convert-timezone.helper';
import { dateFormat } from '@/shared/lib/helpers/date-format.helper';

import { UpdateEmailResponse } from '../model/types/user-api.interface';
import {
	CredsEmailApiVariables,
	CredsEmailsApiVariables,
	PaginationUser,
	PaginationUserResponse,
	UserApiVariables,
	UserFilterVariables,
	UserFilterVariablesResponse,
	UserPagination,
	UsersApiVariables,
} from '../model/types/users-management-api.interface';

const getLegacyUserId = ({
	legacy_user_id,
	user_id,
}: Pick<PaginationUserResponse, 'legacy_user_id' | 'user_id'>): string | undefined => {
	const legacyUserId = legacy_user_id ?? user_id;
	return legacyUserId === undefined || legacyUserId === null ? undefined : String(legacyUserId);
};

const usersManagementApi = userServiceApi.injectEndpoints({
	endpoints: (build) => ({
		getUsers: build.query<UserPagination<PaginationUser>, UsersApiVariables>({
			providesTags: [AppStoreTag.Users],
			query: ({ page, size, value, sortBy, orderBy, userTypes, status }) => {
				const params = new URLSearchParams(
					Object.entries({
						page: String(page),
						size: String(size),
						search: value,
					}),
				);

				if (sortBy) params.append('sort_by', sortBy);
				if (orderBy) params.append('order_by', orderBy);
				if (userTypes) params.append('user_types', userTypes);
				if (status) params.append('user_status', status);

				return {
					method: 'GET',
					url: `user/list?${params}`,
				};
			},
			transformResponse: (
				response: UserPagination<PaginationUserResponse>,
				_,
				{ timezone },
			): UserPagination<PaginationUser> => ({
				...response,
				items: response.items.map((data) => ({
					id: String(data?.id ?? data?.user_id ?? ''),
					legacyUserId: getLegacyUserId(data),
					surname: data.surname,
					name: data.name,
					username: data.username,
					company: data.company,
					accountType: data.account_type,
					scopedRole: data.scoped_role,
					scopeGroupId: data.scope_group_id,
					status: data.status,
					isCredSent: data.are_creds_sent,
					editDashboard: data.edit_dashboard || false,
					email: data.email,
					deletedAt:
						data.deleted_at &&
						dateFormat(convertTimezone(data.deleted_at, timezone), {
							format: 'full',
							dateSeparator: '/',
							separator: '',
						}),
				})),
			}),
		}),
		getUser: build.query<PaginationUser, UserApiVariables>({
			providesTags: [AppStoreTag.UserDetail],
			query: ({ id }) => ({
				method: 'GET',
				url: `user/get/${id}`,
			}),
			transformResponse: (response: PaginationUserResponse, _, { timezone }): PaginationUser => ({
				id: String(response?.id ?? response?.user_id ?? ''),
				legacyUserId: getLegacyUserId(response),
				surname: response.surname,
				name: response.name,
				username: response.username,
				company: response.company,
				accountType: response.account_type,
				scopedRole: response.scoped_role,
				scopeGroupId: response.scope_group_id,
				isActive: response.is_active,
				status: response.status,
				isCredSent: response.are_creds_sent,
				editDashboard: response.edit_dashboard,
				email: response.email,
				groups:
					response.groups?.map(({ id, level, name, node_type, path }) => ({
						id,
						level,
						name,
						nodeType: node_type,
						path,
					})) ?? [],
				acceptedPrivacyPolicyVersion: response.accepted_privacy_policy_version,
				acceptedTermsAndConditionsVersion: response.accepted_terms_and_conditions_version,
				acceptedTimeTC:
					response.tc_accepted_time &&
					dateFormat(convertTimezone(response.tc_accepted_time, timezone), {
						format: 'full',
						dateSeparator: '/',
						separator: '',
					}),
				acceptedTimePP:
					response.pp_accepted_time &&
					dateFormat(convertTimezone(response.pp_accepted_time, timezone), {
						format: 'full',
						dateSeparator: '/',
						separator: '',
					}),
				deletedAt:
					response.deleted_at &&
					dateFormat(convertTimezone(response.deleted_at, timezone), {
						format: 'date',
					}),
			}),
			async onQueryStarted(_, { queryFulfilled, dispatch }) {
				try {
					await queryFulfilled;
					dispatch(boatServiceApi.util.invalidateTags([AppStoreTag.Boat, AppStoreTag.BoatDetail]));
				} catch {}
			},
		}),
		getUsersFilters: build.query<UserFilterVariables, void>({
			query: () => ({
				method: 'GET',
				url: 'user/list/filters',
			}),
			transformResponse: ({ user_types }: UserFilterVariablesResponse) => ({
				userTypes: user_types?.map((value) => ({ value, label: value })) ?? [],
			}),
		}),
		sendCredsEmail: build.mutation<string, CredsEmailApiVariables>({
			invalidatesTags: [AppStoreTag.Users],
			query: (body) => ({
				method: 'POST',
				url: 'user/send-creds',
				body,
			}),
		}),
		sendCredsEmails: build.mutation<void, CredsEmailsApiVariables>({
			invalidatesTags: [AppStoreTag.Users],
			query: (body) => ({
				method: 'POST',
				url: 'user/send-creds/bulk',
				body,
			}),
			async onQueryStarted({ user_ids }, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;
					dispatch(
						toastActions.addToast({
							type: 'success',
							title: t('users:group.management.addPeople.toast.invited.title'),
							message: t('users:group.management.addPeople.toast.invited.message', { count: user_ids.length }),
						}),
					);
				} catch {
					dispatch(
						toastActions.addToast({
							type: 'error',
							title: t('users:group.management.addPeople.toast.invited.error.title'),
							message: t('users:group.management.addPeople.toast.invited.error.message'),
						}),
					);
				}
			},
		}),
		updateEmail: build.mutation<UpdateEmailResponse, { email: string }>({
			query: (body) => ({
				method: 'PATCH',
				url: 'user/update-email',
				body,
			}),
		}),
		exportUsers: build.query<{ data: string }, void>({
			query: () => ({
				method: 'GET',
				url: 'user/export-users',
			}),
		}),
	}),
});

export const {
	useGetUsersQuery,
	useGetUserQuery,
	useLazyGetUserQuery,
	useGetUsersFiltersQuery,
	useSendCredsEmailMutation,
	useSendCredsEmailsMutation,
	useUpdateEmailMutation,
	useExportUsersQuery,
} = usersManagementApi;
